/**
 * Auth Service
 * 🔐 Handles all authentication related API calls
 */
import api from '../../../utils/api';

const API_URL = '/api/auth';

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise} Response from API
 */
const register = async (userData) => {
  try {
    console.log('🚀 Attempting to register user:', userData.email);
    const response = await api.post(`${API_URL}/register`, userData);
    
    // Make sure tokens and user data are properly stored
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      // Add auth header for immediate subsequent requests
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      console.log('🔑 Authentication token stored, user session established');
    } else {
      console.warn('⚠️ No auth token received during registration');
    }
    
    console.log('✅ Registration successful');
    return response.data;
  } catch (error) {
    console.log('❌ Registration failed:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Login user
 * @param {Object} credentials - User login credentials
 * @returns {Promise} Response from API
 */
const login = async (credentials) => {
  try {
    console.log('🔑 Attempting login for user:', credentials.email);
    const response = await api.post(`${API_URL}/login`, credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    console.log('✅ Login successful');
    return response.data;
  } catch (error) {
    console.log('❌ Login failed:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Logout user
 * @returns {Promise} Response from API
 */
const logout = async () => {
  try {
    console.log('🚪 Attempting logout');
    // Clear local storage first to ensure it's always cleared
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Try to call the API, but don't fail if it's not available
    try {
      const response = await api.post(`${API_URL}/logout`);
      console.log('✅ Logout successful');
      return response.data;
    } catch (apiError) {
      // If the API call fails (e.g., 404), just log it and continue
      console.log('⚠️ Logout API call failed, but local storage was cleared:', apiError.response?.data?.message || apiError.message);
      return { success: true, message: 'Logged out locally' };
    }
  } catch (error) {
    console.log('❌ Logout failed:', error.message);
    // Still clear local storage even if something else fails
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return { success: true, message: 'Logged out locally despite error' };
  }
};

/**
 * Get current user profile
 * @returns {Promise} Response from API
 */
const getCurrentUser = async () => {
  try {
    console.log('🔍 Fetching current user profile');
    const response = await api.get(`${API_URL}/me`);
    console.log('✅ User profile fetched successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to fetch user profile:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Generate partner invitation
 * @param {String} partnerEmail - Email of the partner to invite
 * @returns {Promise} Response from API
 */
const generatePartnerInvite = async (partnerEmail) => {
  try {
    console.log('🔗 Generating partner invitation for:', partnerEmail);
    const response = await api.post(`${API_URL}/partner/invite`, { partnerEmail });
    console.log('✅ Partner invitation generated successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to generate partner invitation:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Accept partner invitation
 * @param {String} invitationIdOrToken - ID or token of the invitation to accept
 * @param {Boolean} isToken - Whether the provided parameter is a token instead of an ID
 * @returns {Promise} Response from API
 */
const acceptPartnerInvite = async (invitationIdOrToken, isToken = false) => {
  try {
    console.log('🤝 Accepting partner invitation');
    
    let requestData;
    if (isToken) {
      requestData = { token: invitationIdOrToken };
    } else {
      requestData = { invitationId: invitationIdOrToken };
    }
    
    const response = await api.post(`${API_URL}/partner/accept`, requestData);
    console.log('✅ Partner invitation accepted successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to accept partner invitation:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Reject partner invitation
 * @param {String} invitationId - ID of the invitation to reject
 * @returns {Promise} Response from API
 */
const rejectPartnerInvite = async (invitationId) => {
  try {
    console.log('👎 Rejecting partner invitation');
    const response = await api.post(`${API_URL}/partner/reject`, { invitationId });
    console.log('✅ Partner invitation rejected successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to reject partner invitation:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Get partner invitations
 * @returns {Promise} Response from API
 */
const getPartnerInvitations = async () => {
  try {
    console.log('📬 Getting partner invitations');
    const response = await api.get(`${API_URL}/partner/invitations`);
    console.log('✅ Partner invitations retrieved successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to get partner invitations:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Search users
 * @param {String} searchTerm - Term to search for (email, phone, etc.)
 * @returns {Promise} Response from API
 */
const searchUsers = async (searchTerm) => {
  try {
    console.log('🔍 Searching users with term:', searchTerm);
    const response = await api.get(`${API_URL}/users/search?q=${encodeURIComponent(searchTerm)}`);
    console.log('✅ Users search completed successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to search users:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Send partner invitation
 * @param {String} recipientId - ID of the user to invite
 * @returns {Promise} Response from API
 */
const sendPartnerInvitation = async (recipientId) => {
  try {
    console.log('📨 Sending partner invitation to user ID:', recipientId);
    const response = await api.post(`${API_URL}/partner/invite/user`, { recipientId });
    console.log('✅ Partner invitation sent successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to send partner invitation:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Update privacy settings
 * @param {Object} privacySettings - Privacy settings to update
 * @returns {Promise} Response from API
 */
const updatePrivacySettings = async (privacySettings) => {
  try {
    console.log('🔒 Updating privacy settings');
    const response = await api.put(`${API_URL}/partner/privacy`, { privacySettings });
    console.log('✅ Privacy settings updated successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to update privacy settings:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Update user profile data
 * @param {Object} userData - User data to update (firstName, lastName, phoneNumber, dateOfBirth)
 * @returns {Promise} Response from API
 */
const updateUser = async (userData) => {
  try {
    console.log('👤 Updating user profile:', userData);
    const response = await api.put(`${API_URL}/profile`, userData);
    console.log('✅ User profile updated successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to update user profile:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Change user password
 * @param {String} currentPassword - Current password
 * @param {String} newPassword - New password
 * @returns {Promise} Response from API
 */
const changePassword = async (currentPassword, newPassword) => {
  try {
    console.log('🔐 Changing user password');
    const response = await api.put(`${API_URL}/password`, { 
      currentPassword, 
      newPassword 
    });
    console.log('✅ Password changed successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to change password:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Create partner profile within the user account
 * @param {Object} partnerData - Partner data (firstName, lastName, email, phoneNumber, dateOfBirth)
 * @returns {Promise} Response from API
 */
const createPartnerProfile = async (partnerData) => {
  try {
    console.log('👥 Creating partner profile:', partnerData);
    const response = await api.post(`${API_URL}/partner/create`, partnerData);
    console.log('✅ Partner profile created successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to create partner profile:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Update partner profile data
 * @param {Object} partnerData - Partner data to update
 * @returns {Promise} Response from API
 */
const updatePartnerProfile = async (partnerData) => {
  try {
    console.log('👥 Updating partner profile:', partnerData);
    const response = await api.put(`${API_URL}/partner/profile`, partnerData);
    console.log('✅ Partner profile updated successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to update partner profile:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Remove partner
 * @returns {Promise} Response from API
 */
const removePartner = async () => {
  try {
    console.log('🗑️ Removing partner');
    const response = await api.delete(`${API_URL}/partner/current`);
    console.log('✅ Partner removed successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to remove partner:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Cancel partner invitation
 * @param {String} invitationId - ID of the invitation to cancel
 * @returns {Promise} Response from API
 */
const cancelPartnerInvite = async (invitationId) => {
  try {
    console.log('🚫 Cancelling partner invitation');
    const response = await api.post(`${API_URL}/partner/cancel`, { invitationId });
    console.log('✅ Partner invitation cancelled successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to cancel partner invitation:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Resend partner invitation
 * @param {String} invitationId - ID of the invitation to resend
 * @returns {Promise} Response from API
 */
const resendPartnerInvite = async (invitationId) => {
  try {
    console.log('🔄 Resending partner invitation');
    const response = await api.post(`${API_URL}/partner/resend`, { invitationId });
    console.log('✅ Partner invitation resent successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to resend partner invitation:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Verify email with code
 * @param {Object} verificationData - Email and verification code
 * @returns {Promise} Response from API
 */
const verifyEmail = async (verificationData) => {
  try {
    console.log('📧 Verifying email with code');
    const response = await api.post(`${API_URL}/verify-code`, verificationData);
    console.log('✅ Email verification successful');
    return response.data;
  } catch (error) {
    console.log('❌ Email verification failed:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Resend verification code
 * @param {string} email - User's email address
 * @returns {Promise} Response from API
 */
const resendVerificationCode = async (email) => {
  try {
    console.log('📬 Resending verification code');
    const response = await api.post(`${API_URL}/resend-verification`, { email });
    console.log('✅ Verification code resent successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Failed to resend verification code:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Validate invitation code
 * @param {Object} invitationData - Invitation code and associated email/phone
 * @returns {Promise} Response from API
 */
const validateInvitationCode = async (invitationData) => {
  try {
    console.log('🎟️ Validating invitation code');
    const response = await api.post('/api/invitations/validate', invitationData);
    console.log('✅ Invitation code validated successfully');
    return response.data;
  } catch (error) {
    console.log('❌ Invitation validation failed:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Request an invitation
 * @param {Object} requestData - Request data (firstName, lastName, email, reason)
 * @returns {Promise} Response from API
 */
const requestInvitation = async (requestData) => {
  try {
    console.log('🎟️ Requesting invitation');
    // Convert firstName and lastName to the combined name field expected by the server
    const payload = {
      name: `${requestData.firstName} ${requestData.lastName}`,
      firstName: requestData.firstName,
      lastName: requestData.lastName,
      email: requestData.email,
      phoneNumber: requestData.phoneNumber || null,
      reason: requestData.reason
    };
    console.log('📤 Sending invitation request with payload:', payload);
    const response = await api.post('/api/invitations/request', payload);
    console.log('✅ Invitation request submitted successfully');
    return response.data;
  } catch (error) {
    console.error('❌ Error requesting invitation:', error);
    throw error;
  }
};

/**
 * Gets the authorization header with the token
 * @returns {Object} The auth header object
 */
const getAuthHeader = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  return {
    Authorization: user ? `Bearer ${user.token}` : ''
  };
};

const authService = {
  register,
  login,
  logout,
  getCurrentUser,
  generatePartnerInvite,
  acceptPartnerInvite,
  rejectPartnerInvite,
  cancelPartnerInvite,
  resendPartnerInvite,
  getPartnerInvitations,
  searchUsers,
  sendPartnerInvitation,
  updatePrivacySettings,
  updateUser,
  changePassword,
  createPartnerProfile,
  updatePartnerProfile,
  removePartner,
  verifyEmail,
  resendVerificationCode,
  getAuthHeader,
  validateInvitationCode,
  requestInvitation
};

export default authService;
export { getAuthHeader, validateInvitationCode }; 